import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Search, ArrowRightOutlined as ArrowRight } from "@mui/icons-material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  OutlinedInput,
  IconButton,
  InputAdornment,
  Button,
  CircularProgress,
  Typography,
  Stack,
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Tooltip,
  Autocomplete,
  TextField,
  useTheme,
  Tabs,
  Tab,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ZoomIn as ViewIcon, Download as DownloadIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  getMachines,
  readAsyncStorageValues,
  getAllServiceTickets,
  getAllOldServiceTickets,
  changePriorityForServiceTicket,
  // getServiceTicketsByStatusListAndScheduledBefore,
} from "features/service/service.slice";
import { Links } from "utils/pageLinkNames";
import DataGridTable from "component/DataGridTable";
import { DataGrid } from "@mui/x-data-grid";
import { exportToCsv } from "utils/utilFunctions";
import { toast } from "react-toastify";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import TableSkeleton from "component/Loaders/TableSkeleton";

const ServiceTicketsTab = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { mode } = useSelector((state) => state.globalState);

  const [searchParams, setSearchParams] = useSearchParams();
  const [serviceTickets, setServiceTickets] = useState([]);
  const [showExportAlertDialog, setShowExportAlertDialog] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(1);
  const { user } = useSelector((state) => state.auth);

  const [filteredCounts, setFilteredCounts] = useState({
    all: 0,
    periodic: 0,
    oneTime: 0,
    old: 0,
  });

  const [filters, setFilters] = useState({
    creator: "",
    assigner: "",
    technician: "",
    maintenanceCode: "",
    category: "",
    status: "",
    manufModel: "",
  });

  const [filterOptions, setFilterOptions] = useState({
    overDue: false,
    upComingIn: null,
    status: [],
    category: [],
    manufModel: [],
    assigner: [],
    creator: [],
    technician: [],
    maintenanceCode: [],
  });

  const [filteredData, setFilteredData] = useState([]);

  const { machinesData, appConstants, isLoading, allServiceTickets, allOldServiceTickets, serviceTicketsByStatusListAndScheduledBefore } =
    useSelector((state) => state.service);

  const TAB_CONFIG = {
    1: { key: "all", categories: ["EQUIPMENT_SUPPORT", "TASK", "CALIBRATION", "INSPECTION", "INTERNAL_AUDIT", "PREVENTIVE_MAINTENANCE"] },
    2: { key: "periodic", categories: ["CALIBRATION", "INSPECTION", "INTERNAL_AUDIT", "PREVENTIVE_MAINTENANCE"] },
    3: { key: "oneTime", categories: ["EQUIPMENT_SUPPORT", "TASK"] },
    4: { key: "old", categories: [] },
  };

  useEffect(() => {
    dispatch(readAsyncStorageValues());
    dispatch(getMachines());
    dispatch(getAllServiceTickets());
    dispatch(getAllOldServiceTickets());
  }, [dispatch]);

  const updatePriority = (ticketId, newPriority) => {
    const updateObj = {
      serviceTktId: ticketId,
      Priority: newPriority,
    };
    dispatch(changePriorityForServiceTicket(updateObj)).then((res) => {
      if (res && res.error) {
        toast.error("Failed to update priority!");
      } else if (res && res.payload?.status === 200) {
        toast.success(`Priority updated to ${newPriority} successfully!`, {
          autoClose: 1000,
        });
        dispatch(getAllServiceTickets());
      } else {
        toast.error("Something went wrong!");
      }
    });
  };

  useEffect(() => {
    if (selectedTab === 4 && allOldServiceTickets?.data) loadData(4);
  }, [selectedTab, allOldServiceTickets?.data]);

  useEffect(() => {
    if (allServiceTickets?.data || allOldServiceTickets?.data) {
      loadData(selectedTab);
    }
  }, [selectedTab, allServiceTickets, allOldServiceTickets]);

  const FilterDropdown = ({ label, value, options, onChange }) => {
    const sortedOptions = [...options].sort((a, b) => a.localeCompare(b));
    return (
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        style={{ width: "100%", fontSize: "0.875rem", fontWeight: value ? "normal" : 600, backgroundColor: "#f5f5f5" }}
        renderValue={(selected) => selected || <span style={{ fontWeight: 600, fontSize: "0.800rem" }}>{label}</span>}
      >
        <MenuItem value="" style={{ fontWeight: 600 }}>
          {label}
        </MenuItem>
        {sortedOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    );
  };

  const ticketMatchesFilters = (ticket, assigneUser, assignerUser, creatorUser) => {
    const filters = [
      () => !filterOptions.maintenanceCode?.length || filterOptions.maintenanceCode.includes(ticket?.maintenanceCode),
      () => !filterOptions.category?.length || filterOptions.category.includes(ticket?.ticketCategory),
      () => !filterOptions.status?.length || filterOptions.status.includes(ticket?.status),
      () => !filterOptions.ticketIds?.length || filterOptions.ticketIds.includes(ticket?.ticketId.toString()),
      () => !filterOptions.creator?.length || filterOptions.creator.includes(creatorUser?.name),
      () => !filterOptions.assigner?.length || filterOptions.assigner.includes(assignerUser?.nameEmpno),
      () => !filterOptions.technician?.length || filterOptions.technician.includes(assigneUser?.nameEmpno),
      () => !filterOptions.modelName?.length || filterOptions.modelName.includes(ticket?.modelName),
      () => !filterOptions.manufacturer?.length || filterOptions.manufacturer.includes(ticket?.manufacturer),
      () => {
        if (!filterOptions.manufModel?.length) return true;
        const manufacturer = ticket?.manufacturer || "";
        const modelName = ticket?.modelName || "";
        const combinedValue = manufacturer && modelName ? `${manufacturer} - ${modelName}` : manufacturer || modelName;
        return filterOptions.manufModel.includes(combinedValue);
      },
      () =>
        !filterOptions.upComingIn ||
        (ticket.dueDate && moment(ticket.dueDate).isBetween(moment(), moment().add(parseInt(filterOptions.upComingIn), "d"))),
      () => !filterOptions.upComingIn || ticket.dueDate,
      () => !filterOptions.overDue || moment(moment().format("DD MMM YYYY")).isAfter(moment(ticket.dueDate).format("DD MMM YYYY")),
    ];
    return filters.every((filter) => filter());
  };

  const ticketBelongsToTab = (ticket, tabNumber) => {
    if (tabNumber === 4) return true;

    const config = TAB_CONFIG[tabNumber];
    const categories = config?.categories.map((cat) => appConstants.allTypes.SERVICE_CATEGORY[cat]);
    return categories?.includes(ticket.ticketCategory);
  };

  const transformTicket = (ticket) => {
    const assigneUser = appConstants?.appUsers?.find((user) => ticket?.assigneeId === user.userId);
    //const assignerUser = appConstants?.appUsers?.find((user) => ticket?.assignerId === user.userId);
    const assignerUser = appConstants?.appUsers?.find(
  (user) =>
    ticket?.assignerId === user.userId &&
    user.userRole === appConstants.allTypes.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR
);

    const creatorUser = appConstants?.appUsers?.find((user) => ticket?.creatorId === user.userId);

    return {
      ticketId: ticket?.ticketId,
      ticketNumber: appConstants?.showServiceTicketId ? ticket?.ticketId : ticket?.ticketNumber || ticket?.ticketId,
      ticketCategory: ticket.ticketCategory,
      status: ticket.status,
      machineManufacturer: ticket?.manufacturer || "",
      machineModel: ticket?.modelName || "",
      machineModelId: ticket?.modelId || "",
      machineId: ticket?.machineId || "",
      machineSerialNumber: ticket?.serialNumber || "",
      machineMaintenanceCode: ticket?.maintenanceCode || "",
      description: ticket?.description || "",
      dueDate: ticket.dueDate ? moment(ticket.dueDate).format("DD MMM YY") : null,
      dateCreated: moment(ticket.openDate).format("DD MMM YY"),
      scheduledDate: moment(ticket.scheduledDate).format("DD MMM YY"),
      priority: ticket.priority || null,
      vendor: ticket.vendor || null,
      creator: creatorUser?.name || "",
      assigner: assignerUser?.name || "",
      technician: assigneUser?.name || "",
      safetyTicket: ticket?.safetyTicket || null,
    };
  };

  const rawTicketsForTab = useMemo(() => {
    const data = selectedTab === 4 ? allOldServiceTickets?.data || [] : allServiceTickets?.data || [];
    return data.filter((ticket) => ticketBelongsToTab(ticket, selectedTab));
  }, [selectedTab, allServiceTickets, allOldServiceTickets, appConstants]);

  const filteredTickets = useMemo(() => {
    let tickets = rawTicketsForTab || [];
    if (filters.creator) {
      tickets = tickets.filter((t) => {
        const creatorUser = appConstants?.appUsers?.find((u) => t.creatorId === u.userId);
        return (creatorUser?.name || "") === filters.creator;
      });
    }
    if (filters.assigner) {
      tickets = tickets.filter((t) => {
        const assignerUser = appConstants?.appUsers?.find(
          (u) => t.assignerId === u.userId && u.userRole === appConstants.allTypes.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR
        );
        return (assignerUser?.nameEmpno || assignerUser?.name || "") === filters.assigner;
      });
    }
    if (filters.technician) {
      tickets = tickets.filter((t) => {
        const assigneUser = appConstants?.appUsers?.find((u) => t.assigneeId === u.userId);
        return (assigneUser?.nameEmpno || assigneUser?.name || "") === filters.technician;
      });
    }
    if (filters.maintenanceCode) {
      tickets = tickets.filter((t) => (t.maintenanceCode || "") === filters.maintenanceCode);
    }
    if (filters.category) {
      tickets = tickets.filter((t) => (t.ticketCategory || "") === filters.category);
    }
    if (filters.status) {
      tickets = tickets.filter((t) => (t.status || "") === filters.status);
    }
    if (filters.manufModel) {
      tickets = tickets.filter((t) => {
        const manufacturer = t.manufacturer || "";
        const modelName = t.modelName || "";
        const combined = manufacturer && modelName ? `${manufacturer} - ${modelName}` : manufacturer || modelName;
        return combined === filters.manufModel;
      });
    }
    if (filterOptions.overDue) {
      tickets = tickets.filter((t) => t.dueDate && moment(moment().format("DD MMM YYYY")).isAfter(moment(t.dueDate).format("DD MMM YYYY")));
    }
    if (filterOptions.upComingIn != null) {
      tickets = tickets.filter(
        (t) =>
          t.dueDate && moment(t.dueDate).isBetween(moment(), moment().add(parseInt(filterOptions.upComingIn), "d"))
      );
    }
    if (filterOptions.ticketIds?.length) {
      tickets = tickets.filter((t) => filterOptions.ticketIds.includes(t.ticketId?.toString()));
    }
    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase();
      tickets = tickets.filter((t) => {
        const assigneUser = appConstants?.appUsers?.find((u) => t.assigneeId === u.userId);
        const assignerUser = appConstants?.appUsers?.find(
          (u) => t.assignerId === u.userId && u.userRole === appConstants.allTypes.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR
        );
        const creatorUser = appConstants?.appUsers?.find((u) => t.creatorId === u.userId);
        const searchFields = [
          t.ticketId?.toString(),
          t.ticketNumber?.toString(),
          t.description,
          t.maintenanceCode,
          t.manufacturer,
          t.modelName,
          assigneUser?.name,
          assignerUser?.name,
          creatorUser?.name,
        ];
        return searchFields.some((field) => field?.toLowerCase().includes(searchLower));
      });
    }
    return tickets;
  }, [
    rawTicketsForTab,
    filters,
    filterOptions.overDue,
    filterOptions.upComingIn,
    filterOptions.ticketIds,
    searchValue,
    appConstants,
  ]);

  const { creators, supervisors, technicians, maintenanceCodes, categories, statuses, manufModels } = useMemo(() => {
    const creatorsSet = new Set();
    const supervisorsSet = new Set();
    const techniciansSet = new Set();
    const maintenanceCodesSet = new Set();
    const categoriesSet = new Set();
    const statusesSet = new Set();
    const manufModelsSet = new Set();
    (filteredTickets || []).forEach((ticket) => {
      const creatorUser = appConstants?.appUsers?.find((u) => ticket.creatorId === u.userId);
      if (creatorUser?.name) creatorsSet.add(creatorUser.name);
      const assignerUser = appConstants?.appUsers?.find(
        (u) =>
          ticket.assignerId === u.userId && u.userRole === appConstants.allTypes.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR
      );
      if (assignerUser) supervisorsSet.add(assignerUser.nameEmpno || assignerUser.name);
      const assigneUser = appConstants?.appUsers?.find((u) => ticket.assigneeId === u.userId);
      if (assigneUser) techniciansSet.add(assigneUser.nameEmpno || assigneUser.name);
      if (ticket.maintenanceCode) maintenanceCodesSet.add(ticket.maintenanceCode);
      if (ticket.ticketCategory) categoriesSet.add(ticket.ticketCategory);
      if (ticket.status) statusesSet.add(ticket.status);
      if (ticket.manufacturer && ticket.modelName) manufModelsSet.add(`${ticket.manufacturer} - ${ticket.modelName}`);
    });
    return {
      creators: Array.from(creatorsSet).sort((a, b) => a.localeCompare(b)),
      supervisors: Array.from(supervisorsSet).sort((a, b) => a.localeCompare(b)),
      technicians: Array.from(techniciansSet).sort((a, b) => a.localeCompare(b)),
      maintenanceCodes: Array.from(maintenanceCodesSet).sort((a, b) => a.localeCompare(b)),
      categories: Array.from(categoriesSet).sort((a, b) => a.localeCompare(b)),
      statuses: Array.from(statusesSet).sort((a, b) => a.localeCompare(b)),
      manufModels: Array.from(manufModelsSet).sort((a, b) => a.localeCompare(b)),
    };
  }, [filteredTickets, appConstants]);

  const gridRows = useMemo(() => {
    const rows = (filteredTickets || []).map(transformTicket);
    return rows.sort((a, b) => b.ticketId - a.ticketId);
  }, [filteredTickets, appConstants]);

  const loadData = (selectedTab) => {
    setLoading(true);
    const ticketsToProcess = selectedTab === 4 ? allOldServiceTickets?.data : allServiceTickets?.data;

    if (!ticketsToProcess) {
      setLoading(false);
      return;
    }

    const serviceTickets = ticketsToProcess
      .filter((ticket) => {
        const assigneUser = appConstants?.appUsers?.find((user) => ticket?.assigneeId === user.userId);
        return ticketMatchesFilters(ticket, assigneUser) && ticketBelongsToTab(ticket, selectedTab);
      })
      .map(transformTicket)
      .sort((a, b) => b.ticketId - a.ticketId);

    setServiceTickets(serviceTickets);
    updateFilteredCounts();
    setLoading(false);
  };
  const hasActiveFilters = () => {
    return (
      searchValue.trim() !== "" ||
      filterOptions.overDue ||
      filterOptions.upComingIn != null ||
      (filterOptions.ticketIds && filterOptions.ticketIds.length > 0) ||
      Object.values(filters).some((v) => v !== "")
    );
  };

  const getCountForTab = (tabNumber) => {
    const data = tabNumber === 4 ? allOldServiceTickets?.data : allServiceTickets?.data;
    if (!data) return 0;

    return data.filter((ticket) => ticketBelongsToTab(ticket, tabNumber)).length;
  };

  const initializeAllCounts = () => {
    if (allServiceTickets?.data && appConstants?.allTypes?.SERVICE_CATEGORY) {
      setFilteredCounts({
        all: getCountForTab(1),
        periodic: getCountForTab(2),
        oneTime: getCountForTab(3),
        old: getCountForTab(4),
      });
    }
  };

  const updateFilteredCounts = () => {
    if (!allServiceTickets?.data || !appConstants?.allTypes?.SERVICE_CATEGORY) return;

    const tabKey = TAB_CONFIG[selectedTab]?.key;

    if (!hasActiveFilters()) {
      initializeAllCounts();
      return;
    }

    setFilteredCounts((prev) => ({ ...prev, [tabKey]: filteredTickets.length }));
  };

  useEffect(() => {
    if (allServiceTickets?.data?.length) {
      initializeAllCounts();
    }
  }, [allServiceTickets]);

  useEffect(() => {
    if (allServiceTickets?.data || allOldServiceTickets?.data) {
      updateFilteredCounts();
    }
  }, [selectedTab, allServiceTickets, allOldServiceTickets, filterOptions, searchValue, filters, filteredTickets.length]);

  useEffect(() => {
    const ticketCategoryArrayParam = searchParams.getAll("ticketCategory[]");
    const maintenanceCodeArrayParam = searchParams.getAll("maintenanceCode[]");
    const ticketStatusArrayParam = searchParams.getAll("ticketStatus[]");
    const ticketIdArrayParam = searchParams.getAll("ticketId[]");
    setFilterOptions({
      ...filterOptions,
      category: ticketCategoryArrayParam,
      maintenanceCode: maintenanceCodeArrayParam,
      status: ticketStatusArrayParam,
      ticketIds: ticketIdArrayParam,
    });
  }, []);

  const getCurrentTabData = () => {
    const data = selectedTab === 4 ? allOldServiceTickets?.data || [] : allServiceTickets?.data || [];
    return data.filter((ticket) => ticketBelongsToTab(ticket, selectedTab));
  };

  const renderGridActions = (data) => {
    return (
      <div>
        {/* <IconButton sx={{ mr: 1 }} onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + data.row.ticketId)}>
          <Tooltip title="View">
            <ViewIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton> */}
        <IconButton
          sx={{ mr: 1 }}
          onClick={() => navigate(`/${Links.UPDATE_SERVICE_TICKET}/` + data.row.ticketId, { state: { enableEditing: true } })}
        >
          <Tooltip title="Edit">
            <EditIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
        {data.row.priority === "High" && (
          <Tooltip title="Medium Priority">
            <IconButton sx={{ mr: 1 }} onClick={() => updatePriority(data.row.ticketId, "Medium")}>
              <ArrowDownwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
            </IconButton>
          </Tooltip>
        )}
        {data.row.priority === "Medium" && (
          <span>
            <Tooltip title="High Priority">
              <IconButton onClick={() => updatePriority(data.row.ticketId, "High")}>
                <ArrowUpwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Low Priority">
              <IconButton onClick={() => updatePriority(data.row.ticketId, "Low")}>
                <ArrowDownwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
              </IconButton>
            </Tooltip>
          </span>
        )}

        {data.row.priority === "Low" && (
          <Tooltip title="Medium Priority">
            <IconButton onClick={() => updatePriority(data.row.ticketId, "Medium")}>
              <ArrowUpwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
            </IconButton>
          </Tooltip>
        )}
      </div>
    );
  };

  const handleSearchChange = (event) => setSearchValue(event.target.value);

  const handleClearSearch = () => setSearchValue("");

  const handleTabsChange = (event, newValue) => {
    setSelectedTab(newValue);
    handleClearSearch();
    setFilters({
      creator: "",
      assigner: "",
      technician: "",
      maintenanceCode: "",
      category: "",
      status: "",
      manufModel: "",
    });
    setFilterOptions({
      overDue: false,
      upComingIn: null,
      status: [],
      category: [],
      manufModel: [],
      assigner: [],
      creator: [],
      technician: [],
      maintenanceCode: [],
      ticketIds: [],
    });
  };

  const clearAllFilters = () => {
    setFilters({
      creator: "",
      assigner: "",
      technician: "",
      maintenanceCode: "",
      category: "",
      status: "",
      manufModel: "",
    });
    setFilterOptions({
      maintenanceCode: [],
      manufModel: [],
      creator: [],
      assigner: [],
      technician: [],
      category: [],
      status: [],
      upComingIn: null,
      overDue: false,
      ticketIds: [],
    });
    setSearchValue("");
  };


  const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));
  const columnAttributes = [
    {
      field: "ticketNumber",
      headerName: "Ticket ID",
      minWidth: 60,
      flex: 0.54,
      valueGetter: (params) => {
        return appConstants?.showServiceTicketId ? params.row.ticketId : params.row.ticketNumber;
      },
      renderCell: (params) => {
        const displayValue = appConstants?.showServiceTicketId ? params.row.ticketId : params.value;
        return (
          <span
            style={{
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + params.row.ticketId)}
          >
            {displayValue}
          </span>
        );
      },
    },  
    {
      field: "dateCreated",
      headerName: "Open Date",
      minWidth: 90,
      flex: 0.1,
      sortComparator: dateComparator,
    },
    {
      field: "scheduledDate",
      headerName: "Sch. Date",
      minWidth: 90,
      flex: 0.1,
      sortComparator: dateComparator,
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      minWidth: 90,
      flex: 0.1,
      sortComparator: dateComparator,
    },
    {
      field: "creator",
      headerName: "Creator",
      minWidth: 120,
      flex: 0.6,
      renderHeader: () => (
        <FilterDropdown
          label="Creator"
          value={filters.creator}
          options={creators}
          onChange={(value) => setFilters({ ...filters, creator: value })}
        />
      ),
    },
    {
      field: "assigner",
      headerName: "Supervisor",
      minWidth: 120,
      flex: 0.5,
      renderHeader: () => (
        <FilterDropdown
          label="Supervisor"
          value={filters.assigner}
          options={supervisors}
          onChange={(value) => setFilters({ ...filters, assigner: value })}
        />
      ),
    },
    {
      field: "technician",
      headerName: "Tech",
      minWidth: 120,
      flex: 0.5,
      renderHeader: () => (
        <FilterDropdown
          label="Tech"
          value={filters.technician}
          options={technicians}
          onChange={(value) => setFilters({ ...filters, technician: value })}
        />
      ),
    },
    ...(appConstants?.showManufacturerModelInAssetList === true
      ? [
          {
            field: "manufModel",
            headerName: "Manufacturer & Model",
            minWidth: 100,
            flex: 0.8,
            renderHeader: () => (
              <FilterDropdown
                label="Manf & Model"
                value={filters.manufModel}
                options={manufModels}
                onChange={(value) => setFilters({ ...filters, manufModel: value })}
              />
            ),
            renderCell: (params) => {
              const manufacturer = params.row.machineManufacturer || params.row.manufacturer || "";
              const modelName = params.row.machineModel || params.row.modelName || "";
              const combinedValue = manufacturer && modelName ? `${manufacturer} - ${modelName}` : manufacturer || modelName;

              return (
                <div
                  onClick={() => navigate(`/${Links.MACHINE_MODEL}/${params.row.machineModelId}`)}
                  style={{ cursor: "pointer", textDecoration: "none" }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                >
                  {combinedValue}
                </div>
              );
            },
            valueGetter: (params) => {
              const manufacturer = params.row.machineManufacturer || params.row.manufacturer || "";
              const modelName = params.row.machineModel || params.row.modelName || "";
              return manufacturer && modelName ? `${manufacturer} - ${modelName}` : manufacturer || modelName;
            },
          },
        ]
      : []),
    {
      field: "machineMaintenanceCode",
      headerName: "Maint. Code",
      minWidth: 100,
      editType: "dropdownedit",
      textAlign: "left",
      flex: 0.5,
      renderHeader: () => (
        <FilterDropdown
          label="Maint. Code"
          value={filters.maintenanceCode}
          options={maintenanceCodes}
          onChange={(value) => setFilters({ ...filters, maintenanceCode: value })}
        />
      ),
      renderCell: (params) => {
        return (
          <div
            onClick={() => navigate(`/${Links.EDIT_MACHINE}/${params.row.machineId}`)}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            {params.value}
          </div>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 110,
      editType: "dropdownedit",
      textAlign: "left",
      flex: 0.5,
    },
    {
      field: "ticketCategory",
      headerName: "Category",
      minWidth: 100,
      flex: 0.3,
      renderCell: (params) => {
        const abbreviation = appConstants?.allTypes?.ABBREVIATIONS?.[params.value];
        return abbreviation || params.value;
      },
      renderHeader: () => {
        const abbreviatedOptions = categories.map((cat) => appConstants?.allTypes?.ABBREVIATIONS?.[cat] || cat);
        const displayValue = filters.category ? appConstants?.allTypes?.ABBREVIATIONS?.[filters.category] || filters.category : "";
        return (
          <FilterDropdown
            label="Category"
            value={displayValue}
            options={abbreviatedOptions}
            onChange={(abbreviatedValue) => {
              if (!abbreviatedValue) {
                setFilters({ ...filters, category: "" });
                return;
              }
              const originalCategory =
                categories.find((cat) => appConstants?.allTypes?.ABBREVIATIONS?.[cat] === abbreviatedValue) || abbreviatedValue;
              setFilters({ ...filters, category: originalCategory });
            }}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 0.3,
      renderHeader: () => (
        <FilterDropdown
          label="Status"
          value={filters.status}
          options={statuses}
          onChange={(value) => setFilters({ ...filters, status: value })}
        />
      ),
    },
    {
      field: "vendor",
      headerName: "Vendor",
      minWidth: 90,
      flex: 0.3,
      valueGetter: (params) => {
        const vendorId = params.row?.vendor?.id || params.row?.vendor;
        const customer = appConstants.customers.find((user) => user.id === vendorId);
        return customer?.customerName || "Internal";
      },
    },
    {
      field: "Actions",
      headerName: "Actions",
      width: 140,
      flex: 1.1,
      sortable: false,
      filterable: false,
      renderCell: renderGridActions,
      disableClickEventBubbling: true,
    },
  ];
  const downloadCsv = (e, exportAll = false) => {
    e.preventDefault();
    const serviceTicketsList = [];
    if (exportAll) {
      const baseTickets = rawTicketsForTab || [];
      baseTickets.forEach((ticket) => {
        const machine = machinesData.find((machine) => machine.id === ticket?.machine?.id);
        const assigneUser = appConstants?.appUsers?.find((user) => {
          return ticket?.assignees?.[0] === user.userId;
        });
        const assignerUser = appConstants?.appUsers?.find((user) => ticket?.assignerId === user.userId);
        const creatorUser = appConstants?.appUsers?.find((user) => ticket?.creatorId === user.userId);
        return serviceTicketsList.push({
          ticketNumber: appConstants?.showServiceTicketId ? ticket?.ticketId : ticket?.ticketNumber || ticket?.ticketId,
          ticketCategory: ticket?.ticketCategory,
          status: ticket?.status,
          machineManufacturer: ticket?.manufacturer || "",
          machineModel: ticket?.model || "",
          machineSerialNumber: ticket?.serialNumber || "",
          machineMaintenanceCode: ticket?.maintenanceCode || "",
          description: ticket?.description || "",
          dueDate: moment(ticket.dueDate).format("DD MMM YYYY"),
          dateCreated: moment(ticket.openDate).format("DD MMM YYYY"),
          scheduledDate: moment(ticket.scheduledDate).format("DD MMM YYYY"),
          technician: assigneUser?.name,
          assigner: assignerUser?.nameEmpno || "",
          creator: creatorUser?.name || "",
        });
      });
    }
    const headers = [
      "Ticket ID, Open Date, Scheduled Date, Maintenance Code, Description, Manufacturer,Model, Serial Number, Creator, Supervisor, Technician, Category, Status",
    ];
    const keys = [
      "ticketNumber",
      "dateCreated",
      "scheduledDate",
      "machineMaintenanceCode",
      "description",
      "machineManufacturer",
      "machineModel",
      "machineSerialNumber",
      "creator",
      "assigner",
      "technician",
      "ticketCategory",
      "status",
    ];

    exportToCsv(headers, keys, exportAll ? serviceTicketsList : gridRows, "ServiceTickets");
    setShowExportAlertDialog(false);
  };

  const filterTickets = (filterOption, filterItem) => {
    setFilterOptions({ ...filterOptions, [filterOption]: filterOptions[filterOption].filter((item) => item !== filterItem) });
  };

  const FILTER_LABELS = {
    creator: "Creator",
    assigner: "Supervisor",
    technician: "Tech",
    maintenanceCode: "Maint. Code",
    category: "Category",
    status: "Status",
    manufModel: "Manf & Model",
  };

  const renderAppliedHeaderFilters = () => {
    const entries = Object.entries(filters).filter(([, value]) => value !== "");
    if (entries.length === 0) return null;
    return (
      <Paper>
        <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} px={0.5} py={filterOptions && filterOptions.overDue ? 0 : 0.3}>
          {entries.map(([key, value]) => {
            const displayValue = key === "category" && appConstants?.allTypes?.ABBREVIATIONS?.[value]
              ? appConstants.allTypes.ABBREVIATIONS[value]
              : value;
            return (
              <React.Fragment key={key}>
                <Typography m={1} textTransform={"capitalize"}>
                  {FILTER_LABELS[key]}
                </Typography>
                <Stack direction={"row"} alignItems={"center"} pl={1} borderRadius={10} backgroundColor="#00808030">
                  <Typography variant="caption">{displayValue}</Typography>
                  <IconButton onClick={() => setFilters((prev) => ({ ...prev, [key]: "" }))}>
                    <Tooltip title="Clear item">
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </Tooltip>
                  </IconButton>
                </Stack>
              </React.Fragment>
            );
          })}
        </Stack>
      </Paper>
    );
  };

  const renderCustomFilter = (filterOption) => (
    <Paper>
      {filterOptions &&
      filterOptions[filterOption] &&
      filterOptions[filterOption].length &&
      filterOption !== "category" &&
      filterOption !== "status" &&
      filterOption !== "maintenanceCode" &&
      filterOption !== "ticketIds" &&
      filterOption !== "priority" ? (
        <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} px={0.5} py={filterOptions && filterOptions.overDue ? 0 : 0.3}>
          <Typography m={1} textTransform={"capitalize"}>
            {filterOption}
          </Typography>
          {filterOptions[filterOption].map((filterItem) => (
            <Stack direction={"row"} alignItems={"center"} pl={1} borderRadius={10} backgroundColor="#00808030">
              <Typography variant="caption">{filterItem}</Typography>
              <IconButton onClick={() => filterTickets(filterOption, filterItem)}>
                <Tooltip title="Clear item">
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Tooltip>
              </IconButton>
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Paper>
  );

  return (
    <>
      <Box sx={{ mx: 2 }}>
        <Stack direction={"row"} justifyContent={"space-between"} my={2}>
          <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} gap={2}>
            <Box>
              <Button
                startIcon={<AddIcon />}
                sx={{ height: "self" }}
                autoCapitalize="none"
                variant="contained"
                onClick={() =>
                  navigate(`/${Links.CREATE_SERVICE_TICKET}/`, {
                    state: { isCreatingTicket: true },
                  })
                }
              >
                Create
              </Button>
            </Box>

            <OutlinedInput
              id="outlined-adornment-password"
              value={searchValue}
              onChange={(event) => handleSearchChange(event)}
              placeholder="Search..."
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                searchValue && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} edge="end">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
              sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
            />

            <Paper>
              <Stack
                direction={"row"}
                backgroundColor={
                  filterOptions && filterOptions.overDue
                    ? mode === "dark"
                      ? theme.palette.grey[700]
                      : theme.palette.primary[700]
                    : mode === "dark"
                    ? theme.palette.grey[10]
                    : theme.palette.primary[200]
                }
                alignItems={"center"}
                px={0.5}
                py={filterOptions && filterOptions.overDue ? 0 : 0.3}
              >
                <Tooltip title="Filter over due tickets">
                  <IconButton onClick={() => setFilterOptions({ ...filterOptions, overDue: true })} size="small">
                    <Typography sx={{ color: "#fff" }}>Over due</Typography>
                  </IconButton>
                </Tooltip>
                {filterOptions && filterOptions.overDue ? (
                  <IconButton onClick={() => setFilterOptions({ ...filterOptions, overDue: false })}>
                    <Tooltip title="Clear filter">
                      <CloseIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </Tooltip>
                  </IconButton>
                ) : null}
              </Stack>
            </Paper>

            <Autocomplete
              disablePortal
              size="small"
              id="combo-box-demo"
              onChange={(event, newValue, reason) => {
                setFilterOptions({ ...filterOptions, upComingIn: reason === "clear" ? null : parseInt(newValue) });
              }}
              value={filterOptions && filterOptions.upComingIn ? filterOptions.upComingIn : ""}
              options={[7, 15, 30, 60, 90]}
              sx={{ minWidth: 150, maxWidth: 600 }}
              renderInput={(params) => <TextField {...params} label="Upcoming in" style={{}} />}
            />
            <Button startIcon={<CloseIcon />} sx={{ height: "self" }} autoCapitalize="none" variant="contained" onClick={clearAllFilters}>
              Clear All
            </Button>
            {renderAppliedHeaderFilters()}
            {Object.keys(filterOptions).map((filterOption) => renderCustomFilter(filterOption))}
          </Stack>
          <Stack>
            <Button
              startIcon={<DownloadIcon />}
              sx={{ minWidth: 170 }}
              autoCapitalize="none"
              variant="contained"
              onClick={(e) => {
                if (hasActiveFilters()) setShowExportAlertDialog(true);
                else downloadCsv(e);
              }}
            >
              Export to csv
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={selectedTab} onChange={handleTabsChange} aria-label="service tickets tabs" centered>
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`60 days (${filteredCounts.all})`}
              value={1}
            />
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`Periodic Service Tickets (${filteredCounts.periodic})`}
              value={2}
            />
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`One Time Service Tickets (${filteredCounts.oneTime})`}
              value={3}
            />
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`More Than 60 Days (${filteredCounts.old})`}
              value={4}
            />
          </Tabs>
        </Box>
        <>
          <Box style={{ height: 700, width: "100%", paddingTop: 3, marginTop: 8, overflow: "auto" }}>
            {loading || isLoading ? (
              <TableSkeleton rows={8} columns={columnAttributes.length} />
            ) : (
              <DataGrid
                loading={loading || isLoading}
                getRowId={(row) => row.ticketId}
                rows={gridRows}
                columns={columnAttributes}
                pageSize={5}
                rowsPerPageOptions={[5]}
                disableColumnMenu
                localeText={{ noRowsLabel: "No tickets." }}
              />
            )}
          </Box>
        </>
        <Dialog open={showExportAlertDialog} onClose={() => setShowExportAlertDialog(false)}>
          <DialogContent>
            <Typography mt={1}>Click "Export filtered" to export only the filtered tickets.</Typography>
            <Typography mt={1}>Click "Export all" to export all tickets.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowExportAlertDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={(e) => downloadCsv(e)}>
              Export filtered
            </Button>
            <Button variant="contained" onClick={(e) => downloadCsv(e, true)}>
              Export all
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};
export default ServiceTicketsTab;
